<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import {
    computed, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import type { DataTablePageEvent, DataTableSortEvent } from 'primevue';
import Column from 'primevue/column';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import TabStatPills from '@/components/ui/TabStatPills.vue';
import TabSearch from '@/components/ui/TabSearch.vue';
import PaymentBankFilter from '@/components/payments/BankAccountFilter.vue';
import PaymentTrustedAccountFilter from '@/components/payments/TrustedAccountFilter.vue';
import CheckboxFilter from '@/components/ui/CheckboxFilter.vue';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import AppTable, {
    type AppTableInstance,
    col,
} from '@/components/ui/AppTable.vue';
import ColumnCustomizeControl from '@/components/ui/ColumnCustomizeControl.vue';
import useTableColumns from '@/composables/useTableColumns';
import { savePaymentBankIds } from '@/composables/usePaymentBankOptions';
import { savePaymentTrustedAccountIds } from '@/composables/usePaymentTrustedAccountOptions';
import IconExport from '@/components/icons/IconExport.vue';
import ToolbarIconButton from '@/components/ui/ToolbarIconButton.vue';
import QueuedExportModal from '@/components/modals/QueuedExportModal.vue';
import RejectPaymentModal from '@/components/modals/RejectPaymentModal.vue';
// PHASE2: import ModifyPaymentModal from '@/components/modals/ModifyPaymentModal.vue';
import PaymentsEmptyState from '@/components/payments/PaymentsEmptyState.vue';
import SsnDisplay from '@/components/ui/SsnDisplay.vue';
import {
    PAYMENTS_COLUMNS,
    PAYMENTS_LOCKED_COLUMN_KEYS,
    PAYMENTS_SORTABLE_KEYS,
    type PaymentRow,
    usePayments,
} from '@/composables/usePayments';
import {
    exportPayments,
    mapTableColumnsToExportColumns,
} from '@/services/paymentService';
import {
    PaymentStatus,
    parseStatusQuery,
    type PaymentStatusValue,
} from '@/services/paymentStatus';
import { ApiError } from '@/models/errors/ApiError';
import { formatDisplayDate } from '@/utils/dt';
import { useAuthStore } from '@/stores/auth';
import { useTableColumnPreferencesStore } from '@/stores/tableColumnPreferences';
import { TableColumnPreferenceId } from '@/services/tableColumnPreferenceService';

type PaymentActionMode = 'none' | 'dynamic' | 'reject' | 'modify';
type ProcessDateMode = 'cleared-aware' | 'process-date';

const QUEUED_EXPORT_TITLE = 'Payments export started';
const DEFAULT_QUEUED_EXPORT_MESSAGE = 'Your export is in progress. You’ll get an email with a download link shortly.';
const EXPORT_QUEUED_COOLDOWN_MS = 60_000;

const STATUS_LABEL: Record<PaymentStatusValue, string> = {
    [PaymentStatus.OPEN]: 'Open',
    [PaymentStatus.PENDING]: 'Pending',
    [PaymentStatus.LOW_BALANCE]: 'Low Balance',
    [PaymentStatus.CANCEL]: 'Cancelled',
    [PaymentStatus.CLEARED]: 'Cleared',
};

interface Props {
    /** Statuses this tab requests while the status filter is empty; omit to request every status. */
    baseStatuses?: PaymentStatusValue[];
    /** Statuses selected on first load when the URL does not preselect another valid status. */
    defaultStatuses?: PaymentStatusValue[];
    /** Statuses the status filter offers; omit to hide the filter. */
    statusOptions?: PaymentStatusValue[];
    emptyDescription: string;
    processDateHeader?: string;
    processDateMode?: ProcessDateMode;
    actionMode?: PaymentActionMode;
}

const props = withDefaults(defineProps<Props>(), {
    processDateHeader: 'Process Date',
    processDateMode: 'cleared-aware',
    actionMode: 'none',
});

const tableRef = ref<AppTableInstance | null>(null);
const rejectModalVisible = ref(false);
// PHASE2: const modifyModalVisible = ref(false);
const selectedPaymentId = ref<number | null>(null);
const exporting = ref(false);
const exportCooldownActive = ref(false);
const exportQueuedModalVisible = ref(false);
const exportQueuedTitle = ref(QUEUED_EXPORT_TITLE);
const exportQueuedMessage = ref(DEFAULT_QUEUED_EXPORT_MESSAGE);
const toast = useToast();
const authStore = useAuthStore();
const preferenceStore = useTableColumnPreferencesStore();
const {
    payments: paymentColumnPreference,
    loadedUserId: preferencesLoadedUserId,
    loading: preferencesLoading,
    saving: preferencesSaving,
    resetting: preferencesResetting,
} = storeToRefs(preferenceStore);
let exportCooldownTimer: ReturnType<typeof setTimeout> | null = null;

const route = useRoute();

// A dashboard or report deep link preselects statuses this tab actually offers.
const routeStatuses = parseStatusQuery(route.query.status, props.statusOptions ?? []);
// Dashboard/report deep links are explicit filters; a column preference must not broaden them.
const hasRouteStatusFilter = routeStatuses.length > 0;
const defaultStatuses = parseStatusQuery(
    props.defaultStatuses ?? [],
    props.statusOptions ?? [],
);
const selectedStatuses = ref<PaymentStatusValue[]>(
    routeStatuses.length > 0 ? routeStatuses : defaultStatuses,
);

const effectiveStatuses = computed<PaymentStatusValue[] | undefined>(() => (
    selectedStatuses.value.length > 0 ? selectedStatuses.value : props.baseStatuses
));

const statusOptionLabels = computed(
    () => (props.statusOptions ?? []).map((status) => STATUS_LABEL[status]),
);

const selectedStatusLabels = computed<string[]>({
    get: () => selectedStatuses.value.map((status) => STATUS_LABEL[status]),
    set: (labels) => {
        selectedStatuses.value = (props.statusOptions ?? []).filter(
            (status) => labels.includes(STATUS_LABEL[status]),
        );
    },
});

// PHASE1: Pending-only tabs do not have a Bank value to display or filter.
const showsBank = (
    props.baseStatuses === undefined
    || props.baseStatuses.includes(PaymentStatus.CLEARED)
);
const disabledColumnKeys = showsBank ? [] : ['bank'];

const {
    rows,
    loading,
    hasExactTotals,
    totalRecords,
    stats,
    search,
    // PHASE1: Filter Payments by the displayed Bank column.
    bankIds,
    payeeIds,
    dateRange,
    // PHASE2: bankingAccountIds,
    page,
    limit,
    sortField,
    sortOrder,
    fetchPayments,
    getExportParams,
} = usePayments(effectiveStatuses, showsBank);

const {
    visibleColumnKeys,
    columnOrder,
    orderedVisibleColumnKeys,
    applySettings: applyColumnSettings,
} = useTableColumns(PAYMENTS_COLUMNS, PAYMENTS_LOCKED_COLUMN_KEYS);

const displayedColumnKeys = computed(() => (
    showsBank
        ? orderedVisibleColumnKeys.value
        : orderedVisibleColumnKeys.value.filter((key) => key !== 'bank')
));
const displayedColumnKeySet = computed(() => new Set(displayedColumnKeys.value));
const showBankFilter = computed(() => (
    showsBank && displayedColumnKeySet.value.has('bank')
));
const showTrustedAccountFilter = computed(() => (
    displayedColumnKeySet.value.has('trustedAccount')
));
const showStatusFilter = computed(() => (
    statusOptionLabels.value.length > 0 && displayedColumnKeySet.value.has('status')
));
const columnPreferencesReady = computed(() => {
    const userId = authStore.user?.id;
    return userId !== undefined
        && preferencesLoadedUserId.value === userId
        && !preferencesLoading.value;
});
const columnPreferencesBusy = computed(() => (
    preferencesSaving.value || preferencesResetting.value
));

function resetFiltersForHiddenColumns() {
    if (showsBank && !showBankFilter.value && bankIds.value !== null) {
        bankIds.value = null;
        savePaymentBankIds(null);
    }
    if (!showTrustedAccountFilter.value && payeeIds.value !== null) {
        payeeIds.value = null;
        savePaymentTrustedAccountIds(null);
    }
    if (
        !showStatusFilter.value
        && statusOptionLabels.value.length > 0
        && !hasRouteStatusFilter
    ) {
        selectedStatuses.value = [];
    }
}

watch(paymentColumnPreference, (preference) => {
    applyColumnSettings(preference);
    resetFiltersForHiddenColumns();
}, { immediate: true, deep: true });

watch(() => authStore.user?.id, (userId) => {
    if (userId === undefined) {
        preferenceStore.clear();
        return;
    }
    preferenceStore.load(userId);
}, { immediate: true });

onMounted(() => fetchPayments());

onUnmounted(() => {
    if (exportCooldownTimer !== null) {
        clearTimeout(exportCooldownTimer);
    }
});

function startExportCooldown() {
    if (exportCooldownTimer !== null) {
        clearTimeout(exportCooldownTimer);
    }

    exportCooldownActive.value = true;
    exportCooldownTimer = setTimeout(() => {
        exportCooldownActive.value = false;
        exportCooldownTimer = null;
    }, EXPORT_QUEUED_COOLDOWN_MS);
}

function getColumnHeader(key: string): string {
    if (key === 'processDate') return props.processDateHeader;
    return PAYMENTS_COLUMNS.find((column) => column.key === key)?.label ?? key;
}

function getProcessDate(row: PaymentRow): string {
    if (props.processDateMode === 'process-date') return row.processDate;
    return row.status === 'Cleared' ? row.createdOn : row.processDate;
}

function actionLabel(row: PaymentRow): string {
    if (props.actionMode === 'reject') return 'Reject';
    // PHASE2: if (props.actionMode === 'modify') return 'Modify';
    if (props.actionMode !== 'dynamic') return '';
    if (row.status === 'Pending' || row.status === 'Open') return 'Reject';
    // PHASE2: if (row.status === 'Low Balance') return 'Modify';
    return '';
}

function handleActionClick(row: PaymentRow) {
    const label = actionLabel(row);

    if (label === 'Reject') {
        selectedPaymentId.value = row.id;
        rejectModalVisible.value = true;
    }
    // PHASE2: else if (label === 'Modify') {
    // PHASE2:     modifyModalVisible.value = true;
    // PHASE2: }
}

function handleRejected(paymentId: number) {
    const row = rows.value.find((payment) => payment.id === paymentId);
    if (row) row.status = 'Canceled';
}

function handlePageChange(event: DataTablePageEvent) {
    if (event.rows !== limit.value) {
        fetchPayments(1, event.rows);
        return;
    }

    fetchPayments(event.page + 1, event.rows);
}

function handleSortChange(event: DataTableSortEvent) {
    if (event.sortOrder === 1 || event.sortOrder === -1) {
        sortField.value = event.sortField as string;
        sortOrder.value = event.sortOrder;
    } else {
        sortField.value = '';
        sortOrder.value = null;
    }

    fetchPayments(1, limit.value);
}

async function handleExportCurrentView() {
    if (
        exporting.value
        || exportCooldownActive.value
        || !columnPreferencesReady.value
        || columnPreferencesBusy.value
    ) return;

    // Lock immediately so rapid/double clicks cannot start concurrent exports.
    exporting.value = true;

    try {
        const columns = mapTableColumnsToExportColumns(displayedColumnKeys.value);
        if (columns.length === 0) {
            toast.add({
                severity: 'warn',
                summary: 'Export',
                detail: 'Select at least one table column to export.',
                life: 3000,
            });
            return;
        }

        const result = await exportPayments({
            ...getExportParams(),
            columns,
        });

        if (result.mode === 'queued') {
            exportQueuedMessage.value = result.message || DEFAULT_QUEUED_EXPORT_MESSAGE;
            exportQueuedModalVisible.value = true;
            startExportCooldown();
        } else {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Payments CSV downloaded.',
                life: 3000,
            });
        }
    } catch (error) {
        const apiError = ApiError.from(error);
        const data = apiError?.data as { message?: string } | undefined;
        let detail = 'Unable to export payments. Please try again.';
        if (apiError?.status === 429) {
            detail = 'Too many export requests. Please wait and try again.';
        } else if (typeof data?.message === 'string') {
            detail = data.message;
        }
        toast.add({
            severity: 'error',
            summary: 'Export failed',
            detail,
            life: 4000,
        });
    } finally {
        exporting.value = false;
    }
}

function handleColumnPersistenceError(action: 'save' | 'reset', error: unknown) {
    const apiError = ApiError.from(error);
    const data = apiError?.data as { message?: string } | undefined;
    const fallback = action === 'save'
        ? 'Unable to save column preferences. Please try again.'
        : 'Unable to reset column preferences. Please try again.';

    toast.add({
        severity: 'error',
        summary: action === 'save' ? 'Save failed' : 'Reset failed',
        detail: typeof data?.message === 'string' ? data.message : fallback,
        life: 4000,
    });
}
</script>

<template>
    <div class="payments-tab">
        <div class="payments-tab__toolbar">
            <div class="payments-tab__toolbar-main">
                <TabStatPills :stats="stats" :loading="!hasExactTotals" />
                <!-- Search and filters share a box so that, once the toolbar needs
                     a second row, they move down to it together and the search can
                     give up width to keep the filters on one line. -->
                <div class="payments-tab__toolbar-controls">
                    <TabSearch v-model="search" />
                    <!-- Grouped so the filters stay on one line: either the whole
                         group fits or the whole group moves, never one filter. -->
                    <div class="payments-tab__toolbar-filters">
                        <!-- PHASE1: Checkbox list of deposit banks from GET /api/payments/banks. -->
                        <PaymentBankFilter v-if="showBankFilter" v-model="bankIds" />
                        <PaymentTrustedAccountFilter
                            v-if="showTrustedAccountFilter"
                            v-model="payeeIds"
                        />
                        <CheckboxFilter
                            v-if="showStatusFilter"
                            v-model="selectedStatusLabels"
                            label="Filter by Status"
                            :options="statusOptionLabels"
                        />
                        <!-- PHASE2: Destination Bank Account filter used bankingAccountIds. -->
                        <DateRangePicker
                            v-model="dateRange"
                            label="Filter by Date:"
                            trigger-width="auto"
                            trigger-radius="10px"
                        />
                    </div>
                </div>
            </div>
            <div class="payments-tab__toolbar-actions">
                <ToolbarIconButton
                    ariaLabel="Export payments CSV"
                    data-product-tour="payments-export"
                    :disabled="exporting || exportCooldownActive
                        || !columnPreferencesReady || columnPreferencesBusy"
                    @click="handleExportCurrentView"
                >
                    <IconExport />
                </ToolbarIconButton>
                <ColumnCustomizeControl
                    :columns="PAYMENTS_COLUMNS"
                    :locked-column-keys="PAYMENTS_LOCKED_COLUMN_KEYS"
                    :disabled-column-keys="disabledColumnKeys"
                    v-model:visible-column-keys="visibleColumnKeys"
                    v-model:column-order="columnOrder"
                    :table-id="TableColumnPreferenceId.PAYMENTS"
                    persistence="account"
                    :disabled="!columnPreferencesReady"
                    @persistence-error="handleColumnPersistenceError"
                />
            </div>
        </div>
    </div>
    <AppTable
        ref="tableRef"
        class="payments-tab__table"
        :value="rows"
        data-key="id"
        :loading="loading"
        :paginator-loading="!hasExactTotals"
        :lazy="true"
        :rows="limit"
        :first="(page - 1) * limit"
        :total-records="totalRecords"
        :sort-field="sortField || undefined"
        :sort-order="sortOrder"
        @page="handlePageChange"
        @sort="handleSortChange"
    >
        <template v-for="key in displayedColumnKeys" :key="key">
            <Column
                :field="key"
                :header="getColumnHeader(key)"
                :sortable="PAYMENTS_SORTABLE_KEYS.has(key)"
                v-bind="(key === 'amount' || key === 'processDate') ? col({ align: 'right' }) : {}"
            >
                <template #body="{ data }">
                    <SsnDisplay
                        v-if="key === 'ssn' || key === 'coDebtorSsn'"
                        :ssn="data[key]"
                    />
                    <template v-else-if="key === 'processDate'">
                        {{ formatDisplayDate(getProcessDate(data)) }}
                    </template>
                    <template v-else>
                        {{ data[key] }}
                    </template>
                </template>
            </Column>
        </template>

        <Column
            header=""
            v-bind="col({ fill: true })"
        />
        <!-- PHASE 2: UNCOMMENT THIS WHEN PHASE 2 STARTS — restore action-mode reject/dynamic on the payment tabs so this Reject column shows again. -->
        <Column
            v-if="props.actionMode !== 'none'"
            class="payments-tab__action-col"
            header=""
            v-bind="col({ align: 'right', fill: true })"
        >
            <template #body="{ data }">
                <Button
                    v-if="actionLabel(data)"
                    :label="actionLabel(data)"
                    variant="outlined"
                    class="payments-tab__action-btn"
                    :class="`payments-tab__action-btn--`
                        + `${actionLabel(data).toLowerCase()}`"
                    @click="handleActionClick(data)"
                />
            </template>
        </Column>

        <template #empty>
            <PaymentsEmptyState
                :loading="loading"
                :description="props.emptyDescription"
            />
        </template>
    </AppTable>

    <RejectPaymentModal
        v-model:visible="rejectModalVisible"
        :payment-id="selectedPaymentId"
        @rejected="handleRejected"
    />
    <QueuedExportModal
        v-model:visible="exportQueuedModalVisible"
        :title="exportQueuedTitle"
        :message="exportQueuedMessage"
    />
    <!-- PHASE2: <ModifyPaymentModal v-model:visible="modifyModalVisible" /> -->
</template>

<style lang="scss" scoped>
.payments-tab {
    @include tab-base;

    &__toolbar {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: start;
        column-gap: 10px;
    }

    &__toolbar-main {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px 10px;
        min-width: 0;
    }

    &__toolbar-controls {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 10px;
        min-width: 0;
        .tab-search {
            flex: 1 0 150px;
            max-width: 323px;
        }
    }

    &__toolbar-filters {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        min-width: 0;
    }

    &__toolbar-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .tab-stat-pills {
        flex-wrap: wrap;
    }
}

.payments-tab__action-btn {
    width: 78px !important;
    height: 29px !important;
    border-radius: 6px !important;
    padding: 0 !important;
    font-family: $font-family-archivo !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    line-height: 14px !important;
    justify-content: center;
    box-shadow: none !important;
    text-transform: uppercase;

    &--reject {
        color: $action-reject-color !important;
        border-color: $action-reject-color !important;
        background: transparent !important;

        &:hover {
            background: rgba($action-reject-color, 0.06) !important;
        }
    }

    // PHASE2:
    // &--modify {
    //     color: $main-orange !important;
    //     border-color: $main-orange !important;
    //     background: transparent !important;
    //
    //     &:hover {
    //         background: rgba($main-orange, 0.06) !important;
    //     }
    // }
}

.payments-tab__action-col :deep(.p-column-header-content) {
    display: none;
}
</style>
